import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { useLocation, useNavigate, Link } from 'react-router-dom';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      const top = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    if (href.startsWith('/')) {
      navigate(href);
      return;
    }

    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        scrollToSection(href);
      }, 100);
    } else {
      scrollToSection(href);
    }
  };

  const navLinks = [
    { name: t('nav.whyDoora') || 'Why Doora', href: '#features' },
    { name: t('nav.product') || 'Product', href: '/product' },
    { name: t('nav.research') || 'Research', href: '/research' },
    { name: t('nav.benchmark') || 'Benchmark', href: '#benchmark' },
  ];

  return (
    <motion.header
      data-testid="header"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="fixed top-0 left-0 right-0 z-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`rounded-3xl transition-all duration-300 ease-out ${isScrolled ? 'bg-zinc-950/60 backdrop-blur-xl border border-white/10 shadow-lg mt-2 sm:mt-3 p-2 sm:p-2.5' : 'bg-transparent border border-transparent mt-0 p-0 shadow-none'}`}>
          <div className={`relative z-10 flex items-center justify-between px-2 sm:px-3 ${isScrolled ? 'h-12 sm:h-14' : 'h-14 sm:h-16'}`}>
          {/* Logo */}
          <Link
            to="/"
            data-testid="header-logo"
            className="flex items-center gap-2 sm:gap-3"
            onClick={(e) => {
              if (location.pathname === '/') {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <img
              src="/assets/doora_white.png"
              alt="Doora Logo"
              className="h-8 w-8 sm:h-9 sm:w-9"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                onClick={(e) => handleNavClick(e, link.href)}
                className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-100 transition-colors rounded-lg hover:bg-zinc-800/50"
              >
                {link.name}
              </a>
            ))}
          </nav>

          {/* Right side - Language Toggle + CTA */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Toggle */}
            <button
              data-testid="language-toggle"
              onClick={() => setLanguage(language === 'en' ? 'id' : 'en')}
              className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/50 rounded-lg transition-colors"
            >
              <Globe className="w-4 h-4" />
              <span className="font-medium">{language === 'en' ? 'EN' : 'ID'}</span>
            </button>

            <Button
              data-testid="header-cta-button"
              onClick={(e) => handleNavClick(e, '#demo')}
              className="px-4 py-1.5 rounded-lg font-medium text-sm"
            >
              {t('nav.getDemo') || 'Get Demo'}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <button
              data-testid="language-toggle-mobile"
              onClick={() => setLanguage(language === 'en' ? 'id' : 'en')}
              className="p-2 text-zinc-400 hover:text-zinc-100"
            >
              <Globe className="w-5 h-5" />
            </button>
            <button
              data-testid="mobile-menu-toggle"
              className="p-2 text-zinc-400 hover:text-zinc-100"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-black border-b border-zinc-800"
          >
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="block w-full text-left px-4 py-3 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/50 rounded-lg transition-colors"
                >
                  {link.name}
                </a>
              ))}
              <Button
                onClick={(e) => handleNavClick(e, '#demo')}
                className="w-full mt-4 py-2.5 rounded-lg font-medium"
              >
                {t('nav.getDemo') || 'Get Demo'}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
